'use client';

import * as React from "react";
import {
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Sparkles,
  RotateCw,
  X,
} from "lucide-react";
import { cn } from "../../utils/cn";
import { Button } from "../Button/Button";
import { Card, CardBody, CardFooter, CardHeader, CardTitle } from "../Card/Card";
import { Chip } from "../Chip/Chip";
import { Progress } from "../Progress/Progress";
import { Skeleton } from "../Skeleton/Skeleton";

/**
 * RecapPlayer — MV5 Sprint 2.B design page `21 · Recap Player · MV5` (Figma
 * node 126:2). Renders five explicit states pulled from the design:
 *
 *   V1 Idle       — small chip in the AI-tutor pane, one line + a Play affordance.
 *   V2 Expanded   — segment selector; each row is a chapter + preview excerpt.
 *   V3 Playing    — full-frame player with progress and synced captions.
 *   V4 Ended      — "what to do next" affordances (replay, ask a follow-up).
 *   V5 Loading    — stitching-in-progress, driven by backend WS events.
 *
 * The component is **presentational** — it never fetches. Callers pass in the
 * lecture id, the chapter list, and the current state; playback events come
 * back via callbacks so the parent can bridge to a real HTMLMediaElement
 * (or in Wave 3 a react-player abstraction).
 *
 * Backend contract per Figma V6 `128:23`:
 *   GET   /lectures/{id}/chapters              → Chapter[]
 *   GET   /lectures/{id}/segments?concept=slug → Segment[]
 *   POST  /recaps/stitch                       → { job_id, estimated_time_ms }
 *   GET   /recaps/jobs/{job_id}                → { status, progress_pct, result_audio_url, ... }
 *   WS    recap:subscribe / recap:progress     → progress events
 *
 * The player consumes an already-stitched `audioUrl` in V3. When the caller
 * has kicked a stitch job but the audio isn't ready yet, `state="loading"`
 * with `stitchProgressPct` drives V5. Once the WS reports `ready`, the caller
 * swaps to `state="playing"` and passes the returned `audioUrl`.
 */

export type RecapPlayerState =
  | "idle"
  | "expanded"
  | "playing"
  | "ended"
  | "loading";

export interface RecapChapter {
  id: string;
  sequence: number;
  title: string;
  start_ms: number;
  end_ms: number;
  transcript_excerpt?: string | null;
}

export interface RecapSegment {
  id: string;
  chapter_id: string;
  concept_slug: string;
  start_ms: number;
  end_ms: number;
}

export interface RecapPlayerProps {
  /** Lecture UUID (used only for the aria-label + data-slot). */
  lectureId: string;

  /** Current visual state. Parent owns the state machine. */
  state: RecapPlayerState;

  /** Optional short title of the source lecture (rendered in V1 chip + V3). */
  lectureTitle?: string;

  /** Chapters for V2 selector. When absent V2 shows a skeleton. */
  chapters?: RecapChapter[];

  /** Selected segment ids (rows highlighted in V2, used in the header of V3). */
  selectedSegmentIds?: string[];

  /**
   * Audio URL to play in V3. When the parent is waiting on a stitch job this
   * is null and the component should be in state="loading" instead.
   */
  audioUrl?: string | null;

  /**
   * V5 progress bar value (0-100). null → indeterminate.
   */
  stitchProgressPct?: number | null;

  /**
   * Copy shown next to the V5 progress bar. Follows the backend WS event's
   * status → human label mapping.
   */
  stitchStage?: "extracting" | "stitching" | null;

  /** Called when the V1 chip's Play button is clicked. */
  onPlay?: () => void;

  /** Called when the V1 chip's Expand affordance is triggered. */
  onExpand?: () => void;

  /** Called when the V2 header collapse-affordance is clicked. */
  onCollapse?: () => void;

  /** Called when the user picks segments in V2 and hits "Play recap". */
  onSelect?: (segmentIds: string[]) => void;

  /** Called when playback ends (V3 → V4 transition). */
  onEnd?: () => void;

  /** Called when the V4 "Play again" is clicked. */
  onReplay?: () => void;

  /** Called when the V4 "Ask a follow-up" is clicked. */
  onFollowUp?: () => void;

  /** V4 error message when state="ended" but the last job errored. */
  errorMessage?: string | null;

  className?: string;
}

const stageLabel: Record<
  NonNullable<RecapPlayerProps["stitchStage"]>,
  string
> = {
  extracting: "Finding the relevant segments…",
  stitching: "Stitching your recap together…",
};

function formatMs(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}:${rs.toString().padStart(2, "0")}`;
}

export const RecapPlayer = React.forwardRef<HTMLDivElement, RecapPlayerProps>(
  function RecapPlayer(
    {
      lectureId,
      state,
      lectureTitle,
      chapters,
      selectedSegmentIds,
      audioUrl,
      stitchProgressPct,
      stitchStage,
      onPlay,
      onExpand,
      onCollapse,
      onSelect,
      onEnd,
      onReplay,
      onFollowUp,
      errorMessage,
      className,
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        data-slot="recap-player"
        data-state={state}
        data-lecture-id={lectureId}
        className={cn("w-full", className)}
      >
        {state === "idle" && (
          <IdleChip
            lectureTitle={lectureTitle}
            onPlay={onPlay}
            onExpand={onExpand}
          />
        )}
        {state === "expanded" && (
          <ExpandedSelector
            chapters={chapters}
            initialSelectedIds={selectedSegmentIds}
            lectureTitle={lectureTitle}
            onSelect={onSelect}
            onCollapse={onCollapse}
          />
        )}
        {state === "playing" && (
          <PlayingFrame
            lectureTitle={lectureTitle}
            audioUrl={audioUrl ?? null}
            onEnd={onEnd}
          />
        )}
        {state === "loading" && (
          <LoadingFrame
            stitchProgressPct={stitchProgressPct ?? null}
            stitchStage={stitchStage ?? null}
          />
        )}
        {state === "ended" && (
          <EndedFrame
            lectureTitle={lectureTitle}
            errorMessage={errorMessage ?? null}
            onReplay={onReplay}
            onFollowUp={onFollowUp}
          />
        )}
      </div>
    );
  },
);
RecapPlayer.displayName = "RecapPlayer";

// ── V1 · Idle chip ─────────────────────────────────────────────────────────

function IdleChip({
  lectureTitle,
  onPlay,
  onExpand,
}: {
  lectureTitle?: string;
  onPlay?: () => void;
  onExpand?: () => void;
}) {
  return (
    <div
      data-slot="recap-player-idle"
      className={cn(
        "flex items-center gap-space-8 px-space-12 py-space-8",
        "bg-chrome-panel border border-chrome-border rounded-chip",
      )}
    >
      <Sparkles className="size-4 text-accent-violet shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-caption-12 text-ink-muted uppercase tracking-[0.06em]">
          Recap available
        </p>
        <p className="text-body-14 text-ink-primary truncate">
          {lectureTitle ?? "This lecture"}
        </p>
      </div>
      <Button
        size="S"
        variant="primary"
        onClick={onPlay}
        aria-label="Play recap"
        data-slot="recap-player-idle-play"
      >
        <Play className="size-3" />
        Play
      </Button>
      <Button
        size="S"
        variant="ghost"
        onClick={onExpand}
        aria-label="Choose segments"
        data-slot="recap-player-idle-expand"
      >
        <ChevronDown className="size-3" />
      </Button>
    </div>
  );
}

// ── V2 · Expanded segment selector ─────────────────────────────────────────

function ExpandedSelector({
  chapters,
  initialSelectedIds,
  lectureTitle,
  onSelect,
  onCollapse,
}: {
  chapters?: RecapChapter[];
  initialSelectedIds?: string[];
  lectureTitle?: string;
  onSelect?: (ids: string[]) => void;
  onCollapse?: () => void;
}) {
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set(initialSelectedIds ?? []),
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const total = chapters?.length ?? 0;
  const loading = !chapters;

  return (
    <Card
      variant="lecture"
      className="w-full"
      data-slot="recap-player-expanded"
    >
      <CardHeader>
        <div className="flex-1 min-w-0">
          <p className="text-caption-12 text-ink-muted uppercase tracking-[0.06em]">
            Recap selector
          </p>
          <CardTitle className="truncate">
            {lectureTitle ?? "Choose what to recap"}
          </CardTitle>
        </div>
        <Button
          size="S"
          variant="ghost"
          onClick={onCollapse}
          aria-label="Collapse recap selector"
        >
          <ChevronUp className="size-3" />
        </Button>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex flex-col gap-space-8">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : total === 0 ? (
          <p className="text-body-14 text-ink-muted">
            No chapters yet for this lecture. Try again once the recap pipeline
            has processed it.
          </p>
        ) : (
          <ul
            role="listbox"
            aria-multiselectable="true"
            aria-label="Recap segments"
            className="flex flex-col gap-space-4"
          >
            {chapters!.map((c) => {
              const isSelected = selected.has(c.id);
              return (
                <li key={c.id}>
                  <button
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(c.id)}
                    data-slot="recap-player-segment-row"
                    data-selected={isSelected || undefined}
                    className={cn(
                      "w-full text-left flex items-start gap-space-8",
                      "px-space-12 py-space-8 rounded-card border",
                      "transition-colors duration-micro ease-standard",
                      isSelected
                        ? "border-accent-violet bg-chrome-panel"
                        : "border-chrome-border hover:border-accent-violet",
                    )}
                  >
                    <Chip variant="vertical" className="shrink-0">
                      {`Ch ${c.sequence}`}
                    </Chip>
                    <div className="flex-1 min-w-0">
                      <p className="text-body-14 text-ink-primary truncate">
                        {c.title}
                      </p>
                      {c.transcript_excerpt ? (
                        <p className="text-caption-12 text-ink-muted line-clamp-2">
                          {c.transcript_excerpt}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className="text-caption-12 text-ink-muted shrink-0"
                      aria-hidden="true"
                    >
                      {formatMs(c.end_ms - c.start_ms)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
      <CardFooter>
        <p className="text-caption-12 text-ink-muted">
          {selected.size} of {total} selected
        </p>
        <Button
          size="M"
          variant="primary"
          disabled={selected.size === 0}
          onClick={() => onSelect?.(Array.from(selected))}
          data-slot="recap-player-play-selected"
        >
          <Play className="size-3" />
          Play recap
        </Button>
      </CardFooter>
    </Card>
  );
}

// ── V3 · Playing full-frame ────────────────────────────────────────────────

function PlayingFrame({
  lectureTitle,
  audioUrl,
  onEnd,
}: {
  lectureTitle?: string;
  audioUrl: string | null;
  onEnd?: () => void;
}) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [position, setPosition] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  React.useEffect(() => {
    const a = audioRef.current;
    if (!a || !audioUrl) return;
    const onTimeUpdate = () => setPosition(a.currentTime * 1000);
    const onLoadedMeta = () => setDuration(a.duration * 1000);
    const onEnded = () => {
      setIsPlaying(false);
      onEnd?.();
    };
    a.addEventListener("timeupdate", onTimeUpdate);
    a.addEventListener("loadedmetadata", onLoadedMeta);
    a.addEventListener("ended", onEnded);
    // Try to autoplay when the audioUrl first becomes available; if the
    // browser blocks it (user has not gestured yet), the play button below
    // is the fallback.
    void a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    return () => {
      a.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("loadedmetadata", onLoadedMeta);
      a.removeEventListener("ended", onEnded);
    };
  }, [audioUrl, onEnd]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play();
      setIsPlaying(true);
    } else {
      a.pause();
      setIsPlaying(false);
    }
  };

  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <Card
      variant="lecture"
      className="w-full"
      data-slot="recap-player-playing"
    >
      <CardHeader>
        <Sparkles className="size-5 text-accent-violet shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-caption-12 text-ink-muted uppercase tracking-[0.06em]">
            Playing recap
          </p>
          <CardTitle className="truncate">
            {lectureTitle ?? "Your recap"}
          </CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex items-center gap-space-12">
          <Button
            size="L"
            variant="primary"
            onClick={toggle}
            aria-label={isPlaying ? "Pause" : "Play"}
            data-slot="recap-player-playpause"
          >
            {isPlaying ? (
              <Pause className="size-4" />
            ) : (
              <Play className="size-4" />
            )}
          </Button>
          <div className="flex-1 min-w-0 flex flex-col gap-space-4">
            <Progress value={pct} size={6} />
            <div className="flex items-center justify-between text-caption-12 text-ink-muted">
              <span>{formatMs(position)}</span>
              <span>{formatMs(duration)}</span>
            </div>
          </div>
        </div>
        {audioUrl ? (
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            data-slot="recap-player-media"
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── V5 · Loading / stitching ───────────────────────────────────────────────

function LoadingFrame({
  stitchProgressPct,
  stitchStage,
}: {
  stitchProgressPct: number | null;
  stitchStage: "extracting" | "stitching" | null;
}) {
  return (
    <Card
      variant="lecture"
      className="w-full"
      data-slot="recap-player-loading"
    >
      <CardHeader>
        <Sparkles className="size-5 text-accent-violet shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-caption-12 text-ink-muted uppercase tracking-[0.06em]">
            Preparing recap
          </p>
          <CardTitle>
            {stitchStage
              ? stageLabel[stitchStage]
              : "Preparing your recap…"}
          </CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <Progress
          value={stitchProgressPct}
          size={6}
          tone="default"
          label={
            stitchStage
              ? stageLabel[stitchStage]
              : "Preparing recap"
          }
        />
        <p className="mt-space-8 text-caption-12 text-ink-muted">
          Usually takes a few seconds. You can keep browsing — we'll ping you
          when it's ready.
        </p>
      </CardBody>
    </Card>
  );
}

// ── V4 · Ended · what to do next ───────────────────────────────────────────

function EndedFrame({
  lectureTitle,
  errorMessage,
  onReplay,
  onFollowUp,
}: {
  lectureTitle?: string;
  errorMessage: string | null;
  onReplay?: () => void;
  onFollowUp?: () => void;
}) {
  const isError = !!errorMessage;
  return (
    <Card
      variant={isError ? "quiz" : "lecture"}
      className="w-full"
      data-slot="recap-player-ended"
      data-error={isError || undefined}
    >
      <CardHeader>
        {isError ? (
          <X className="size-5 text-status-error shrink-0" />
        ) : (
          <Sparkles className="size-5 text-accent-violet shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-caption-12 text-ink-muted uppercase tracking-[0.06em]">
            {isError ? "Recap failed" : "Recap complete"}
          </p>
          <CardTitle className="truncate">
            {isError
              ? "We couldn't stitch that recap"
              : (lectureTitle ?? "Nice — what next?")}
          </CardTitle>
        </div>
      </CardHeader>
      {isError ? (
        <CardBody>
          <p className="text-body-14 text-ink-primary">
            {errorMessage}
          </p>
        </CardBody>
      ) : null}
      <CardFooter>
        <Button
          size="M"
          variant="secondary"
          onClick={onReplay}
          data-slot="recap-player-replay"
        >
          <RotateCw className="size-3" />
          {isError ? "Try again" : "Play again"}
        </Button>
        <Button
          size="M"
          variant="primary"
          onClick={onFollowUp}
          data-slot="recap-player-followup"
        >
          <Sparkles className="size-3" />
          Ask a follow-up
        </Button>
      </CardFooter>
    </Card>
  );
}
